import { FormControl, MenuItem, Select, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MobileStepper from "@mui/material/MobileStepper";
import TextField from "@mui/material/TextField";
import { useFormik } from "formik";
import { isEmpty } from "lodash";
import { useSnackbar } from "notistack";
import * as React from "react";
import "./index.css";
import * as yup from "yup";
import { applicationInfoType } from "types/app";
import API from "@/api/serverCall";
import getOrganizationInfoRequestThunk from "@/redux/organization/thunk";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

interface AppNameProps {
	activeStep: number;
	setApplicationInfo: (value: applicationInfoType[]) => void;
	setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type appInfoType = {
	id: number;
	name: string;
	packageAndroid: string;
	packageIos: string;
	isFoodLens: boolean | number;
};

const AppName: React.FC<AppNameProps> = ({
	activeStep,
	setApplicationInfo,
	setDialogOpen,
}) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { enqueueSnackbar } = useSnackbar();
	const validationSchema = yup.object({
		name: yup.string().trim().required(t("application_create.validation_app_name")),
	});
	const registApp = (appInfo: appInfoType) => {
		// for loading
		try {
			const requestData = {
				...appInfo,
				id: 0,
			};
			API.POST<applicationInfoType>({
				link: `/app`, // 두잉랩서버 로그인토큰발급 api임
				body: requestData,
			})
				.then((response) => {
					if (!isEmpty(response.data)) {
						setApplicationInfo([response.data]);
						dispatch(getOrganizationInfoRequestThunk());
					}
				})
				.catch((error) => {
					if (error.response.status === 409) {
						enqueueSnackbar("이미 존재하는 패키지명입니다", {
							variant: "info",
						});
					} else if (error.response.status === 411) {
						enqueueSnackbar("생성가능 앱 한도가 초과되었습니다", {
							variant: "info",
						});
					} else if (error.response.status === 400) {
						enqueueSnackbar("패키지명이 모두 비어있을 수 없습니다.", {
							variant: "info",
						});
					} else {
						enqueueSnackbar("일시적 오류입니다", {
							variant: "info",
						});
					}
				});
		} catch (error) {
			console.error(error);
		}
	};

	const formik = useFormik({
		initialValues: {
			id: 0,
			name: "",
			packageAndroid: "",
			packageIos: "",
			isFoodLens: 1,
		},
		validationSchema,
		onSubmit: (values) => {
			registApp({
				id: 0,
				name: values.name,
				packageAndroid: values.packageAndroid,
				packageIos: values.packageIos,
				isFoodLens: Boolean(values.isFoodLens),
			});
		},
	});
	return (
		<>
			<form onSubmit={formik.handleSubmit}>
				<DialogTitle
					sx={{
						marginTop: "2rem",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}
				>
					<Typography
						sx={{
							fontSize: "2rem",
							fontWeight: "bold",
						}}
					>
						{t("application_create.title")}
					</Typography>
				</DialogTitle>

				<DialogContent sx={{ padding: "5rem 3rem 3rem 3rem" }}>
					<div style={{}}>
						<Typography sx={{ color: "#0d0d0d94", textAlign: "left" }}>
							{t("application_create.app_name_title")}
						</Typography>
						<TextField
							variant="outlined"
							size="medium"
							fullWidth={true}
							id="name"
							name="name"
							placeholder={t("application_create.app_name_placeholder")}
							value={formik.values.name}
							onChange={formik.handleChange}
							error={formik.touched.name && Boolean(formik.errors.name)}
							helperText={formik.touched.name && formik.errors.name}
						/>
					</div>
					<div style={{ marginTop: "3rem" }}>
						<div>
							<span style={{ marginBottom: "0.5rem", display: "block" }}>
								{t("application_create.package_name_title")}
							</span>
							<p
								style={{
									color: "#0d0d0d94",
									fontSize: "0.9rem",
									lineHeight: "1.1rem",
								}}
							>
								{t("application_create.package_name_guide")}
							</p>
						</div>

						<div
							style={{
								display: "flex",
								alignItems: "center",
								marginTop: "3rem",
							}}
						>
							<span
								style={{
									display: "block",
									marginRight: "2rem",
									width: "5rem",
								}}
							>
								Android
							</span>
							<TextField
								variant="outlined"
								size="medium"
								fullWidth={true}
								id="packageAndroid"
								name="packageAndroid"
								placeholder="Package Name"
								value={formik.values.packageAndroid}
								onChange={formik.handleChange}
								error={
									formik.touched.packageAndroid &&
									Boolean(formik.errors.packageAndroid)
								}
								helperText={
									formik.touched.packageAndroid &&
									formik.errors.packageAndroid
								}
							/>
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								marginTop: "1rem",
							}}
						>
							<span
								style={{
									display: "block",
									marginRight: "2rem",
									width: "5rem",
								}}
							>
								iOS
							</span>
							<TextField
								variant="outlined"
								size="medium"
								fullWidth={true}
								id="packageIos"
								name="packageIos"
								placeholder="Bundle Identifier"
								value={formik.values.packageIos}
								onChange={formik.handleChange}
								error={
									formik.touched.packageIos &&
									Boolean(formik.errors.packageIos)
								}
								helperText={
									formik.touched.packageIos && formik.errors.packageIos
								}
							/>
						</div>

						<div style={{ marginTop: "3rem" }}>
							<span style={{ marginBottom: "0.5rem", display: "block" }}>
								{t("application_create.api_title")}
							</span>
							<FormControl fullWidth>
								<Select
									labelId="demo-simple-select-label"
									id="demo-simple-select"
									value={formik.values.isFoodLens}
									onChange={(selectedOption) => {
										formik.setFieldValue(
											"isFoodLens",
											selectedOption.target.value,
										);
									}}
								>
									<MenuItem value={1}>FoodLens 1.0</MenuItem>
									<MenuItem value={0}>CaloAI (FoodLens 2.0)</MenuItem>
								</Select>
							</FormControl>
						</div>
					</div>
				</DialogContent>
				<div
					style={{
						margin: "0 2rem 2rem 2rem",
					}}
				>
					<MobileStepper
						variant="dots"
						steps={2}
						position="static"
						activeStep={activeStep}
						backButton={
							<Button
								size="small"
								variant="text"
								style={{
									color: "#2453F9",
									marginRight: "1rem",
									height: "2.5rem",
									padding: "1rem",
								}}
								onClick={() => setDialogOpen(false)}
							>
								{t("application_create.skip")}
							</Button>
						}
						nextButton={
							<Button
								size="small"
								type="submit"
								style={{
									backgroundColor: "#2453F9",
									color: "white",
									marginRight: "1rem",
									height: "2.5rem",
									padding: "1rem",
								}}
							>
								{t("application_create.create")}
							</Button>
						}
					/>
				</div>
			</form>
		</>
	);
};
export default AppName;
